import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { ChevronLeft, ChevronRight, X, Tv, Wifi, Bath, Coffee, Key, Sparkles } from 'lucide-react';

const WHATSAPP_URL = 'https://wa.me/56961964694?text=Hola%20Hotel%20Montecarlo,%20quisiera%20consultar%20disponibilidad%20para%20una%20reserva';

const Rooms = () => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [selectedRoom, setSelectedRoom] = useState(null);
  const [isAutoPlaying, setIsAutoPlaying] = useState(true);

  const rooms = [
    {
      title: 'Habitación Single/Matrimonial',
      video: 'https://customer-assets.emergentagent.com/job_hotel-redesign-1/artifacts/c8d5trn8_video%20Habitacion%20MATRIMONIAL%208672.mp4',
      description: 'Una cama de 2 plazas, televisión por cable, servicio de mucama, Wi-Fi, desayuno continental, cerradura digital y baño privado.',
      amenities: ['TV Cable', 'WiFi', 'Baño Privado', 'Desayuno Continental', 'Cerradura Digital', 'Servicio Mucama']
    },
    {
      title: 'Habitación Triple Matrimonial',
      video: 'https://customer-assets.emergentagent.com/job_hotel-redesign-1/artifacts/lqruo9la_video%20TRIPLE%20MATRIMONIAL%208390.mp4',
      description: 'Una cama de 2 plazas y una cama de 1 plaza, televisión por cable, servicio de mucama, Wi-Fi, desayuno continental, cerradura digital y baño privado.',
      amenities: ['TV Cable', 'WiFi', 'Baño Privado', 'Desayuno Continental', 'Cerradura Digital', 'Servicio Mucama']
    },
    {
      title: 'Habitación Doble (Twin)',
      video: 'https://customer-assets.emergentagent.com/job_hotel-redesign-1/artifacts/8h6p6y96_video%20Habitaci%C3%B3n%20DOBLE%20TWIN%208473.mp4',
      description: 'Dos camas de 1 plaza, televisión por cable, servicio de mucama, Wi-Fi, desayuno continental, cerradura digital y baño privado.',
      amenities: ['TV Cable', 'WiFi', 'Baño Privado', 'Desayuno Continental', 'Cerradura Digital', 'Servicio Mucama']
    },
    {
      title: 'Habitación Triple',
      video: 'https://customer-assets.emergentagent.com/job_hotel-redesign-1/artifacts/gdhstyeg_video%20Habitacion%20TRIPLE.mp4',
      description: 'Tres camas de 1 plaza, televisión por cable, servicio de mucama, Wi-Fi, desayuno continental, cerradura digital y baño privado.',
      amenities: ['TV Cable', 'WiFi', 'Baño Privado', 'Desayuno Continental', 'Cerradura Digital', 'Servicio Mucama']
    },
    {
      title: 'Habitación Cuádruple Matrimonial',
      video: 'https://customer-assets.emergentagent.com/job_hotel-redesign-1/artifacts/ghj8pyns_Video%20Habitaci%C3%B3n%20CU%C3%81DRUPLE%208567.mp4',
      description: 'Una cama de 2 plazas y dos camas de 1 plaza, televisión por cable, servicio de mucama, Wi-Fi, desayuno continental, cerradura digital y baño privado.',
      amenities: ['TV Cable', 'WiFi', 'Baño Privado', 'Desayuno Continental', 'Cerradura Digital', 'Servicio Mucama']
    }
  ];

  const amenityIcons = {
    'TV Cable': Tv,
    'WiFi': Wifi,
    'Baño Privado': Bath,
    'Desayuno Continental': Coffee,
    'Cerradura Digital': Key,
    'Servicio Mucama': Sparkles
  };

  const nextSlide = useCallback(() => {
    setCurrentSlide((prev) => (prev + 1) % rooms.length);
  }, [rooms.length]);

  const prevSlide = () => {
    setCurrentSlide((prev) => (prev - 1 + rooms.length) % rooms.length);
  };

  // Auto-play carousel every 3 seconds
  useEffect(() => {
    if (!isAutoPlaying || selectedRoom) return;
    
    const interval = setInterval(nextSlide, 3000);
    return () => clearInterval(interval);
  }, [isAutoPlaying, selectedRoom, nextSlide]);

  const handleBooking = () => {
    window.open(WHATSAPP_URL, '_blank');
  };

  const openModal = (room) => {
    setSelectedRoom(room);
    setIsAutoPlaying(false);
  };

  const closeModal = () => {
    setSelectedRoom(null);
    setIsAutoPlaying(true);
  };

  return (
    <section id="rooms" className="py-20 bg-white">
      <div className="container mx-auto px-4">
        <div className="text-center mb-12">
          <h2 className="text-4xl md:text-5xl font-bold text-blue-900 mb-4">
            Nuestras Habitaciones
          </h2>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto">
            Habitaciones cómodas y equipadas con todos los servicios para garantizar su descanso
          </p>
        </div>

        {/* Carousel Container */}
        <div 
          className="relative max-w-6xl mx-auto"
          onMouseEnter={() => setIsAutoPlaying(false)}
          onMouseLeave={() => setIsAutoPlaying(true)}
        >
          {/* Main Carousel */}
          <div className="overflow-hidden rounded-2xl shadow-2xl">
            <div 
              className="flex transition-transform duration-500 ease-out"
              style={{ transform: `translateX(-${currentSlide * 100}%)` }}
            >
              {rooms.map((room, index) => (
                <div 
                  key={index} 
                  className="w-full flex-shrink-0 cursor-pointer"
                  onClick={() => openModal(room)}
                  data-testid={`room-slide-${index}`}
                >
                  <div className="relative h-[500px] md:h-[550px] lg:h-[600px]">
                    <video
                      autoPlay
                      loop
                      muted
                      playsInline
                      loading="lazy"
                      className="w-full h-full object-cover"
                    >
                      <source src={room.video} type="video/mp4" />
                    </video>
                    <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/50 to-transparent"></div>
                    <div className="absolute bottom-0 left-0 right-0 p-4 md:p-8 lg:p-10 text-white">
                      <h3 className="text-xl md:text-3xl lg:text-4xl font-bold mb-2 md:mb-3">{room.title}</h3>
                      <p className="text-xs md:text-sm lg:text-base text-white/90 max-w-2xl mb-3 md:mb-4">
                        {room.description}
                      </p>
                      
                      {/* Servicios incluidos - visible en web y móvil */}
                      <div className="mb-3 md:mb-4">
                        <p className="text-amber-400 text-xs md:text-sm font-semibold mb-2">Servicios incluidos:</p>
                        <div className="flex flex-wrap gap-1.5 md:gap-2">
                          {room.amenities.map((amenity, idx) => {
                            const Icon = amenityIcons[amenity] || Coffee;
                            return (
                              <div 
                                key={idx} 
                                className="flex items-center gap-1 md:gap-1.5 bg-white/10 backdrop-blur-sm px-2 md:px-3 py-1 md:py-1.5 rounded-full"
                              >
                                <Icon className="w-3 h-3 md:w-4 md:h-4 text-amber-400" />
                                <span className="text-white text-[10px] md:text-xs font-medium">{amenity}</span>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                      
                      <span className="inline-block text-amber-400 text-xs md:text-sm font-medium hover:text-amber-300 transition-colors">
                        Click para más detalles →
                      </span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Navigation Arrows */}
          <button
            onClick={(e) => { e.stopPropagation(); prevSlide(); }}
            className="absolute left-2 md:left-4 top-1/2 -translate-y-1/2 bg-white/20 backdrop-blur-sm hover:bg-white/40 text-white p-2 md:p-3 rounded-full transition-all z-10"
            data-testid="rooms-prev-btn"
          >
            <ChevronLeft className="w-5 h-5 md:w-6 md:h-6" />
          </button>
          <button
            onClick={(e) => { e.stopPropagation(); nextSlide(); }}
            className="absolute right-2 md:right-4 top-1/2 -translate-y-1/2 bg-white/20 backdrop-blur-sm hover:bg-white/40 text-white p-2 md:p-3 rounded-full transition-all z-10"
            data-testid="rooms-next-btn"
          >
            <ChevronRight className="w-5 h-5 md:w-6 md:h-6" />
          </button>

          {/* Dot Indicators */}
          <div className="flex justify-center mt-6 gap-2">
            {rooms.map((_, index) => (
              <button
                key={index}
                onClick={() => setCurrentSlide(index)}
                className={`h-2 rounded-full transition-all duration-300 ${
                  currentSlide === index ? 'w-8 bg-blue-900' : 'w-2 bg-gray-300 hover:bg-gray-400'
                }`}
                data-testid={`room-dot-${index}`}
              />
            ))}
          </div>
        </div>

        {/* Room Cards Preview */}
        <div className="mt-12 grid grid-cols-2 md:grid-cols-5 gap-3 md:gap-4 max-w-5xl mx-auto">
          {rooms.map((room, index) => (
            <button
              key={index}
              onClick={() => { setCurrentSlide(index); openModal(room); }}
              className={`relative rounded-lg overflow-hidden h-20 md:h-24 transition-all duration-300 ${
                currentSlide === index ? 'ring-2 ring-amber-500 scale-105' : 'opacity-70 hover:opacity-100'
              }`}
              data-testid={`room-thumb-${index}`}
            >
              <video
                muted
                playsInline
                loading="lazy"
                className="w-full h-full object-cover"
              >
                <source src={room.video} type="video/mp4" />
              </video>
              <div className="absolute inset-0 bg-black/40 flex items-center justify-center">
                <span className="text-white text-xs md:text-sm font-medium text-center px-1 leading-tight">
                  {room.title.split(' ').slice(1).join(' ')}
                </span>
              </div>
            </button>
          ))}
        </div>

        <div className="mt-8 text-center">
          <Button
            onClick={handleBooking}
            size="lg"
            className="bg-amber-600 hover:bg-amber-700 text-white text-lg px-10 py-6 rounded-full font-semibold shadow-xl transition-all hover:scale-105"
            data-testid="rooms-main-booking-btn"
          >
            Reservar Ahora
          </Button>
        </div>
      </div>

      {/* Modal for Room Details */}
      {selectedRoom && (
        <div 
          className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-2 md:p-4"
          onClick={closeModal}
        >
          <div 
            className="bg-white rounded-2xl max-w-4xl w-full max-h-[95vh] overflow-y-auto shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Modal Header with Video */}
            <div className="relative h-40 md:h-56 sticky top-0">
              <video
                autoPlay
                loop
                muted
                playsInline
                className="w-full h-full object-cover"
              >
                <source src={selectedRoom.video} type="video/mp4" />
              </video>
              <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent"></div>
              <button
                onClick={closeModal}
                className="absolute top-3 right-3 bg-white/20 backdrop-blur-sm hover:bg-white/40 text-white p-2 rounded-full transition-all"
                data-testid="modal-close-btn"
              >
                <X className="w-5 h-5 md:w-6 md:h-6" />
              </button>
              <h3 className="absolute bottom-3 left-4 md:left-6 text-xl md:text-2xl font-bold text-white">
                {selectedRoom.title}
              </h3>
            </div>

            {/* Modal Content - Scrollable */}
            <div className="p-4 md:p-6">
              <p className="text-gray-700 text-sm md:text-base leading-relaxed mb-4">
                {selectedRoom.description}
              </p>

              <div className="border-t pt-4">
                <h4 className="text-base md:text-lg font-bold text-blue-900 mb-3">Servicios incluidos:</h4>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-2 md:gap-3">
                  {selectedRoom.amenities.map((amenity, idx) => {
                    const Icon = amenityIcons[amenity] || Coffee;
                    return (
                      <div key={idx} className="flex items-center gap-2 bg-gray-50 p-2 md:p-3 rounded-lg">
                        <div className="w-8 h-8 md:w-10 md:h-10 bg-blue-900 rounded-full flex items-center justify-center flex-shrink-0">
                          <Icon className="w-4 h-4 md:w-5 md:h-5 text-amber-400" />
                        </div>
                        <span className="text-gray-700 text-xs md:text-sm font-medium">{amenity}</span>
                      </div>
                    );
                  })}
                </div>
              </div>

              <div className="mt-4 p-3 bg-amber-50 rounded-xl">
                <p className="text-amber-800 text-xs md:text-sm font-medium">
                  Desayuno Continental: 08:00 - 10:00 am hrs
                </p>
              </div>

              <div className="mt-4 md:mt-6 flex flex-col sm:flex-row gap-3">
                <Button
                  onClick={handleBooking}
                  className="flex-1 bg-amber-600 hover:bg-amber-700 text-white font-semibold py-4 md:py-6"
                  data-testid="modal-booking-btn"
                >
                  Reservar Esta Habitación
                </Button>
                <Button
                  onClick={closeModal}
                  variant="outline"
                  className="flex-1 border-2 border-blue-900 text-blue-900 hover:bg-blue-900 hover:text-white py-4 md:py-6"
                >
                  Seguir Explorando
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </section>
  );
};

export default Rooms;
